// Putting audio into a jam's pool from the browser.
//
// Hash the bytes here, ask for a signed PUT (its URL carries the hash as a
// checksum R2 enforces), stream them up through this app's own worker
// (/api/account/object, which never buffers), then have the API confirm the
// object against the hash (`/api/jam/:id/files/:hash/verify`). Neither worker
// ever holds the file.
//
// This replaced `POST /api/jam/:id/audio` for the web app. That route takes
// the whole WAV as one request body and buffers it — the web worker once to
// forward it, the API again plus a copy to hash — so a 40MB+ file pushed the
// API past its 128MB memory limit and Cloudflare killed the request
// ("Internal error."). Small files fit, big ones didn't: it looked random.

import type { JamFileSignResponse, JamFileVerifyResponse } from "@shared/api-types.js";
import { json, webApi } from "@web-shared/lib/web-api";
import { encodePreviewOf } from "@upload-core/preview-encode.js";

/** Store `bytes` for `jamId` and return the pool key the room should assert. */
export async function putPoolAudio(jamId: string, bytes: Uint8Array): Promise<{ key: string; hash: string }> {
  const jam = encodeURIComponent(jamId);
  const hash = await sha256Hex(bytes);
  // Bytes the pool already holds need no PUT; the sign call takes the jam's
  // reference itself.
  const signed = await webApi<JamFileSignResponse>(`/jam/${jam}/files`, json("POST", { hash, sizeBytes: bytes.byteLength }));
  if (signed.exists) {
    if (!signed.previewExists) void shipPreview(jam, hash, bytes);
    return { key: signed.key, hash };
  }
  if (!signed.putUrl) throw new Error("No upload URL for this audio.");

  // The checksum header is signed into the URL: the worker forwards it and R2
  // refuses any body that does not hash to it.
  const put = await fetch("/api/account/object", {
    method: "PUT",
    headers: { ...signed.putHeaders, "X-Upload-Url": signed.putUrl, "Content-Type": "application/octet-stream" },
    body: bytes as unknown as BodyInit,
  });
  if (!put.ok) throw new Error(`The audio would not upload (${put.status}). Try again.`);

  // Nothing references bytes the API has not confirmed against their hash.
  const verified = await webApi<JamFileVerifyResponse>(`/jam/${jam}/files/${hash}/verify`, json("POST", {}));
  void shipPreview(jam, hash, bytes);
  return { key: verified.key, hash };
}

/** What a room draws this audio's waveform from. Behind the upload, not part of
 * it: audio with no preview draws later, from the whole file. Never throws. */
async function shipPreview(jam: string, hash: string, bytes: Uint8Array): Promise<void> {
  try {
    const mp3 = await encodePreviewOf(bytes);
    if (!mp3 || mp3.byteLength === 0) return;
    await webApi(`/jam/${jam}/files/${hash}/preview`, {
      method: "POST",
      headers: { "Content-Type": "audio/mpeg" },
      body: mp3 as unknown as BodyInit,
    });
  } catch (err) {
    console.warn(`[pool-audio] preview of ${hash.slice(0, 8)} failed:`, err);
  }
}

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes as unknown as BufferSource);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}
